import React, { useEffect, useRef, useState } from 'react';
import { getEntryMultiplierValue } from '../utils/dreamBoost';
import apiService from '../services/api';

const STYLE_URL = 'https://tiles.openfreemap.org/styles/liberty';
const MAPLIBRE_JS = 'https://unpkg.com/maplibre-gl@5/dist/maplibre-gl.js';
const MAPLIBRE_CSS = 'https://unpkg.com/maplibre-gl@5/dist/maplibre-gl.css';
const MAP_USER_ZOOM = 13;
const MAP_USER_SPAN_DEG = 0.001;
const MAP_INITIAL_NEARBY_STORES = 4;
const MAP_MAX_ZOOM = 19;
const MAP_CLUSTER_MAX_ZOOM = 5;
const MAP_CLUSTER_RADIUS = 56;
const MAP_OFFERS_PAGE_SIZE = 200;
const PAN_DEBOUNCE_MS = 600;
const PIN_MASK =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 40 44' preserveAspectRatio='none'%3E%3Cpath fill='%23000' d='M20 0C31.046 0 40 8.954 40 20C40 26.5 31 34 20 44C9 34 0 26.5 0 20C0 8.954 8.954 0 20 0Z'/%3E%3C/svg%3E\")";

let mapLibrePromise = null;

function loadMapLibre() {
  if (window.maplibregl) return Promise.resolve(window.maplibregl);
  if (!mapLibrePromise) {
    mapLibrePromise = new Promise((resolve, reject) => {
      if (!document.querySelector('link[data-hc-maplibre]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = MAPLIBRE_CSS;
        link.dataset.hcMaplibre = '1';
        document.head.appendChild(link);
      }
      const script = document.createElement('script');
      script.src = MAPLIBRE_JS;
      script.async = true;
      script.onload = () => {
        if (window.maplibregl) resolve(window.maplibregl);
        else {
          mapLibrePromise = null;
          reject(new Error('maplibre'));
        }
      };
      script.onerror = () => {
        mapLibrePromise = null;
        reject(new Error('maplibre'));
      };
      document.head.appendChild(script);
    });
  }
  return mapLibrePromise;
}

function listFrom(payload, keys) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  for (let i = 0; i < keys.length; i += 1) {
    if (Array.isArray(payload[keys[i]])) return payload[keys[i]];
  }
  return [];
}

function logoOf(item) {
  return item?.logoUrl || item?.logo || item?.small_logo_url || item?.large_logo_url || '';
}

export function normalizeLocalStores(payload) {
  const rows = listFrom(payload, ['cardlinked', 'stores', 'offers', 'results', 'data']);
  const seen = {};
  const stores = [];
  rows.forEach((item) => {
    if (!item) return;
    const id = item.id != null ? String(item.id) : item.storeId != null ? String(item.storeId) : '';
    if (!id || seen[id]) return;
    seen[id] = true;
    const latitude = Number(item.latitude);
    const longitude = Number(item.longitude);
    const rawDistance = item.distance;
    const distance = rawDistance == null || rawDistance === '' ? null : Number(rawDistance);
    stores.push({
      id,
      name: item.name || item.storeName || 'Store',
      logo: logoOf(item),
      address: item.address || '',
      city: item.city || '',
      state: item.state || '',
      distance: Number.isFinite(distance) ? distance : null,
      latitude: Number.isFinite(latitude) ? latitude : null,
      longitude: Number.isFinite(longitude) ? longitude : null,
      entryMultiplier: getEntryMultiplierValue(item),
    });
  });
  return stores;
}

function milesBetween(lat1, lon1, lat2, lon2) {
  const radius = 3959;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return radius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function computeRegion(userLat, userLng, points) {
  const merchants = (points || []).filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng));
  if (Number.isFinite(userLat) && Number.isFinite(userLng) && merchants.length) {
    const closest = merchants
      .map((point) => ({
        lat: point.lat,
        lng: point.lng,
        distance: milesBetween(userLat, userLng, point.lat, point.lng),
      }))
      .sort((a, b) => a.distance - b.distance)
      .slice(0, MAP_INITIAL_NEARBY_STORES);
    const lats = closest.map((point) => point.lat);
    const lngs = closest.map((point) => point.lng);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    return {
      centerLat: (minLat + maxLat) / 2,
      centerLng: (minLng + maxLng) / 2,
      spanLat: Math.max((maxLat - minLat) * 1.15, 0.004),
      spanLon: Math.max((maxLng - minLng) * 1.15, 0.004),
    };
  }
  if (Number.isFinite(userLat) && Number.isFinite(userLng)) {
    return {
      centerLat: userLat,
      centerLng: userLng,
      spanLat: MAP_USER_SPAN_DEG,
      spanLon: MAP_USER_SPAN_DEG,
    };
  }
  if (merchants.length) {
    const lats = merchants.map((point) => point.lat);
    const lngs = merchants.map((point) => point.lng);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    return {
      centerLat: (minLat + maxLat) / 2,
      centerLng: (minLng + maxLng) / 2,
      spanLat: Math.max((maxLat - minLat) * 1.3, 0.01),
      spanLon: Math.max((maxLng - minLng) * 1.3, 0.01),
    };
  }
  return null;
}

function boundsFromRegion(box) {
  return [
    [box.centerLng - box.spanLon / 2, box.centerLat - box.spanLat / 2],
    [box.centerLng + box.spanLon / 2, box.centerLat + box.spanLat / 2],
  ];
}

function mercator(lng, lat) {
  const x = (Number(lng) + 180) / 360;
  const clamped = Math.max(-85.051128, Math.min(85.051128, Number(lat)));
  const s = Math.sin((clamped * Math.PI) / 180);
  const y = 0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI);
  return { x, y };
}

function clusterPins(stores, zoom) {
  const items = (stores || []).filter((store) => Number.isFinite(store.latitude) && Number.isFinite(store.longitude));
  if (zoom > MAP_CLUSTER_MAX_ZOOM || items.length <= 1) {
    return items.map((store) => ({
      id: store.id,
      cluster: false,
      store,
      lng: store.longitude,
      lat: store.latitude,
    }));
  }
  const z = Math.max(0, Math.floor(zoom));
  const cell = MAP_CLUSTER_RADIUS / (512 * 2 ** z);
  const buckets = {};
  items.forEach((store) => {
    const xy = mercator(store.longitude, store.latitude);
    const key = `${z}:${Math.floor(xy.x / cell)}:${Math.floor(xy.y / cell)}`;
    if (!buckets[key]) buckets[key] = [];
    buckets[key].push(store);
  });
  return Object.keys(buckets).map((key) => {
    const group = buckets[key];
    if (group.length === 1) {
      return {
        id: group[0].id,
        cluster: false,
        store: group[0],
        lng: group[0].longitude,
        lat: group[0].latitude,
      };
    }
    const lat = group.reduce((sum, store) => sum + store.latitude, 0) / group.length;
    const lng = group.reduce((sum, store) => sum + store.longitude, 0) / group.length;
    return { id: `c:${key}`, cluster: true, count: group.length, members: group, lng, lat };
  });
}

function pinNode(store, selected, onSelect) {
  const size = selected ? 44 : 40;
  const border = selected ? 3 : 2.5;
  const root = document.createElement('button');
  root.type = 'button';
  root.className = 'shop-pin';
  root.style.setProperty('--shop-pin-size', `${size}px`);
  root.style.setProperty('--shop-pin-border', `${border}px`);
  root.style.setProperty('--shop-pin-mask', PIN_MASK);
  const head = document.createElement('span');
  head.className = 'shop-pin-head';
  if (store.logo) {
    const img = document.createElement('img');
    img.src = store.logo;
    img.alt = '';
    head.appendChild(img);
  } else {
    head.textContent = String(store.name || '?').trim().charAt(0).toUpperCase() || '?';
  }
  root.appendChild(head);
  root.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    onSelect(store.id);
  });
  return root;
}

function clusterNode(entry, map, onZoom) {
  const count = entry.count;
  const size = count >= 25 ? 52 : count >= 10 ? 46 : 40;
  const root = document.createElement('button');
  root.type = 'button';
  root.className = 'shop-cluster';
  root.style.width = `${size}px`;
  root.style.height = `${size}px`;
  root.textContent = String(count);
  root.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    onZoom();
    const points = entry.members || [];
    if (!points.length || !window.maplibregl) return;
    try {
      const bounds = points.reduce(
        (box, store) => box.extend([store.longitude, store.latitude]),
        new window.maplibregl.LngLatBounds()
      );
      map.fitBounds(bounds, { padding: 64, maxZoom: MAP_MAX_ZOOM, duration: 400 });
    } catch (error) {
      map.easeTo({
        center: [points[0].longitude, points[0].latitude],
        zoom: Math.min(map.getZoom() + 2, MAP_MAX_ZOOM),
      });
    }
  });
  return root;
}

function userPinNode() {
  const root = document.createElement('div');
  root.className = 'shop-user-pin';
  root.innerHTML =
    '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="36" viewBox="0 0 28 36" aria-hidden="true">' +
    '<path fill="#007AFF" stroke="#ffffff" stroke-width="1.5" d="M14 0C6.268 0 0 6.268 0 14c0 10.5 14 22 14 22s14-11.5 14-22C28 6.268 21.732 0 14 0z"/>' +
    '</svg>';
  return root;
}

function cardLocation(store) {
  const parts = [store.city, store.state, 'United States'].filter((part) => part && String(part).trim());
  if (store.city || store.state) return parts.join(', ');
  return store.address || '';
}

function directionLinks(store, origin) {
  const query =
    Number.isFinite(store.latitude) && Number.isFinite(store.longitude)
      ? `${store.latitude},${store.longitude}`
      : [store.address, store.city, store.state].filter(Boolean).join(', ') || store.name;
  const originStr =
    origin && Number.isFinite(origin.latitude) && Number.isFinite(origin.longitude)
      ? `${origin.latitude},${origin.longitude}`
      : '';
  const google =
    `https://www.google.com/maps/dir/?api=1${originStr ? `&origin=${encodeURIComponent(originStr)}` : ''}` +
    `&destination=${encodeURIComponent(query)}`;
  const apple =
    `https://maps.apple.com/?${originStr ? `saddr=${encodeURIComponent(originStr)}&` : ''}` +
    `daddr=${encodeURIComponent(query)}&dirflg=d`;
  return { google, apple };
}

function ShopMap({ stores, origin, ready, selectedId, onSelect, onOrigin, onRegionStores }) {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const userMarkerRef = useRef(null);
  const pinsRef = useRef(new Map());
  const storesRef = useRef(stores);
  const originRef = useRef(origin);
  const readyRef = useRef(ready);
  const selectedRef = useRef(selectedId);
  const onSelectRef = useRef(onSelect);
  const onOriginRef = useRef(onOrigin);
  const onRegionRef = useRef(onRegionStores);
  const framedRef = useRef(false);
  const lastCenterRef = useRef(null);
  const ignoreUntilRef = useRef(0);
  const regionTimerRef = useRef(0);
  const regionStateRef = useRef({ pending: null, inFlight: false });
  const aliveRef = useRef(true);
  const drawRef = useRef(() => {});
  const [failed, setFailed] = useState(false);
  const [finding, setFinding] = useState(false);
  const [locating, setLocating] = useState(false);
  const [picker, setPicker] = useState(false);

  storesRef.current = stores;
  originRef.current = origin;
  readyRef.current = ready;
  selectedRef.current = selectedId;
  onSelectRef.current = onSelect;
  onOriginRef.current = onOrigin;
  onRegionRef.current = onRegionStores;

  const selected = selectedId ? pinsRef.current.get(selectedId) || (stores || []).find((store) => store.id === selectedId) : null;

  useEffect(() => {
    aliveRef.current = true;
    let map = null;
    let observer = null;

    const clearMarkers = () => {
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];
    };

    const findStore = (id) => {
      if (!id) return null;
      if (pinsRef.current.has(id)) return pinsRef.current.get(id);
      return (storesRef.current || []).find((store) => store.id === id) || null;
    };

    const selectStore = (id) => {
      ignoreUntilRef.current = Date.now() + 400;
      onSelectRef.current?.(id);
    };

    const dismissStore = (force) => {
      if (!force && Date.now() < ignoreUntilRef.current) return;
      setPicker(false);
      onSelectRef.current?.('');
    };

    const syncUserMarker = () => {
      if (!map || !window.maplibregl) return;
      const loc = originRef.current;
      const show = loc && Number.isFinite(loc.latitude) && Number.isFinite(loc.longitude);
      if (!show) {
        if (userMarkerRef.current) {
          userMarkerRef.current.remove();
          userMarkerRef.current = null;
        }
        return;
      }
      if (userMarkerRef.current) {
        userMarkerRef.current.setLngLat([loc.longitude, loc.latitude]);
        return;
      }
      userMarkerRef.current = new window.maplibregl.Marker({ element: userPinNode(), anchor: 'bottom' })
        .setLngLat([loc.longitude, loc.latitude])
        .addTo(map);
    };

    const draw = () => {
      if (!map || !window.maplibregl) return;
      clearMarkers();
      clusterPins([...pinsRef.current.values()], map.getZoom()).forEach((entry) => {
        const element = entry.cluster
          ? clusterNode(entry, map, () => dismissStore(true))
          : pinNode(entry.store, entry.store.id === selectedRef.current, selectStore);
        const marker = new window.maplibregl.Marker({
          element,
          anchor: entry.cluster ? 'center' : 'bottom',
        })
          .setLngLat([entry.lng, entry.lat])
          .addTo(map);
        markersRef.current.push(marker);
      });
    };
    drawRef.current = draw;

    const rememberCenter = () => {
      if (!map) return;
      const center = map.getCenter();
      lastCenterRef.current = { lat: center.lat, lng: center.lng };
    };

    const frameMap = () => {
      if (!map) return;
      const points = [...pinsRef.current.values()].map((store) => ({ lat: store.latitude, lng: store.longitude }));
      const loc = originRef.current;
      const box = computeRegion(loc?.latitude, loc?.longitude, points);
      if (box && (points.length || (loc && Number.isFinite(loc.latitude)))) {
        try {
          map.fitBounds(boundsFromRegion(box), { padding: 24, maxZoom: 16, animate: false, duration: 0 });
          return;
        } catch (error) {
          void error;
        }
      }
      if (loc && Number.isFinite(loc.latitude) && Number.isFinite(loc.longitude)) {
        map.jumpTo({ center: [loc.longitude, loc.latitude], zoom: MAP_USER_ZOOM });
      }
    };

    const mergePins = (list) => {
      (list || []).forEach((store) => {
        if (!store?.id || !Number.isFinite(store.latitude) || !Number.isFinite(store.longitude)) return;
        pinsRef.current.set(store.id, store);
      });
    };

    const requestRegion = (lat, lng) => {
      const state = regionStateRef.current;
      state.pending = { lat, lng };
      if (state.inFlight) return;
      state.inFlight = true;
      if (aliveRef.current) setFinding(true);

      const step = async () => {
        const region = state.pending;
        state.pending = null;
        if (!region || cancelled) {
          state.inFlight = false;
          if (!cancelled) setFinding(false);
          return;
        }
        try {
          const raw = await apiService.getOliveOffers({
            latitude: region.lat,
            longitude: region.lng,
            page: 1,
            pageSize: MAP_OFFERS_PAGE_SIZE,
            includeOnline: false,
          });
          if (cancelled) {
            state.inFlight = false;
            return;
          }
          const list = normalizeLocalStores(raw);
          mergePins(list);
          onRegionRef.current?.(list);
          draw();
        } catch (error) {
          void error;
        }
        step();
      };
      step();
    };

    const handleMoveEnd = () => {
      if (mapRef.current !== map) return;
      draw();
      if (!framedRef.current || !lastCenterRef.current) return;
      const center = map.getCenter();
      const bounds = map.getBounds();
      const latSpan = Math.abs(bounds.getNorth() - bounds.getSouth());
      const last = lastCenterRef.current;
      const movedLat = Math.abs(center.lat - last.lat);
      const movedLng = Math.abs(center.lng - last.lng);
      const threshold = Math.max(Number.isFinite(latSpan) ? latSpan : 0.05, 0.01) * 0.25;
      if (movedLat < threshold && movedLng < threshold) return;
      if (regionTimerRef.current) window.clearTimeout(regionTimerRef.current);
      regionTimerRef.current = window.setTimeout(() => {
        lastCenterRef.current = { lat: center.lat, lng: center.lng };
        requestRegion(center.lat, center.lng);
      }, PAN_DEBOUNCE_MS);
    };

    const tryFrame = () => {
      if (!map || !map.loaded() || !readyRef.current || framedRef.current) return;
      mergePins(storesRef.current);
      syncUserMarker();
      frameMap();
      framedRef.current = true;
      rememberCenter();
      draw();
    };

    mapRef.current = null;
    let cancelled = false;
    loadMapLibre()
      .then((maplibregl) => {
        if (cancelled || !elRef.current) return;
        map = new maplibregl.Map({
          container: elRef.current,
          style: STYLE_URL,
          center: [-98.35, 39.5],
          zoom: 3,
          maxZoom: MAP_MAX_ZOOM,
          attributionControl: false,
        });
        mapRef.current = map;
        map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
        map.on('load', () => {
          tryFrame();
          map.on('moveend', handleMoveEnd);
          map.on('click', () => dismissStore(false));
          map.on('dragstart', () => dismissStore(true));
          map.resize();
        });
        if (typeof ResizeObserver !== 'undefined') {
          observer = new ResizeObserver(() => {
            if (mapRef.current) mapRef.current.resize();
          });
          observer.observe(elRef.current);
        }
      })
      .catch(() => {
        if (aliveRef.current) setFailed(true);
      });

    mapRef.currentFrame = tryFrame;
    mapRef.currentSyncUser = syncUserMarker;
    mapRef.currentMerge = mergePins;
    mapRef.currentFrameMap = frameMap;
    mapRef.currentRemember = rememberCenter;
    mapRef.currentDraw = draw;
    mapRef.currentFind = findStore;

    return () => {
      cancelled = true;
      aliveRef.current = false;
      mapRef.currentFrame = null;
      mapRef.currentSyncUser = null;
      mapRef.currentMerge = null;
      mapRef.currentFrameMap = null;
      mapRef.currentRemember = null;
      mapRef.currentDraw = null;
      mapRef.currentFind = null;
      regionStateRef.current = { pending: null, inFlight: false };
      if (regionTimerRef.current) window.clearTimeout(regionTimerRef.current);
      if (observer) observer.disconnect();
      clearMarkers();
      if (userMarkerRef.current) userMarkerRef.current.remove();
      userMarkerRef.current = null;
      if (map) map.remove();
      mapRef.current = null;
      framedRef.current = false;
      lastCenterRef.current = null;
      pinsRef.current = new Map();
    };
  }, []);

  useEffect(() => {
    if (typeof mapRef.currentMerge === 'function') mapRef.currentMerge(stores);
    const alreadyFramed = framedRef.current;
    if (typeof mapRef.currentFrame === 'function') mapRef.currentFrame();
    if (alreadyFramed && typeof mapRef.currentDraw === 'function') mapRef.currentDraw();
    if (typeof mapRef.currentSyncUser === 'function') mapRef.currentSyncUser();
  }, [stores, origin, ready]);

  useEffect(() => {
    setPicker(false);
    const store = typeof mapRef.currentFind === 'function' ? mapRef.currentFind(selectedId) : null;
    if (typeof mapRef.currentDraw === 'function') mapRef.currentDraw();
    const map = mapRef.current;
    if (!selectedId || !store || !map || !map.loaded()) return;
    if (!Number.isFinite(store.latitude) || !Number.isFinite(store.longitude)) return;
    ignoreUntilRef.current = Date.now() + 400;
    map.easeTo({
      center: [store.longitude, store.latitude],
      zoom: MAP_USER_ZOOM,
      duration: 400,
    });
  }, [selectedId]);

  const recenter = () => {
    if (!navigator.geolocation || locating) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const next = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
        originRef.current = next;
        onSelectRef.current?.('');
        if (typeof mapRef.currentSyncUser === 'function') mapRef.currentSyncUser();
        try {
          const raw = await apiService.getOliveOffers({
            latitude: next.latitude,
            longitude: next.longitude,
            page: 1,
            pageSize: MAP_OFFERS_PAGE_SIZE,
            includeOnline: false,
          });
          if (!aliveRef.current) return;
          const list = normalizeLocalStores(raw);
          pinsRef.current = new Map();
          if (typeof mapRef.currentMerge === 'function') mapRef.currentMerge(list);
          framedRef.current = false;
          if (typeof mapRef.currentFrameMap === 'function') mapRef.currentFrameMap();
          framedRef.current = true;
          if (typeof mapRef.currentRemember === 'function') mapRef.currentRemember();
          if (typeof mapRef.currentDraw === 'function') mapRef.currentDraw();
          onOriginRef.current?.(next);
          onRegionRef.current?.(list);
        } catch (error) {
          void error;
        }
        if (aliveRef.current) setLocating(false);
      },
      () => {
        if (aliveRef.current) setLocating(false);
        window.alert('Location is unavailable. Allow location access and try again.');
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const links = selected ? directionLinks(selected, origin) : null;

  if (failed) {
    return <div className="shop-map shop-map-empty">Map is unavailable right now.</div>;
  }

  return (
    <div className="shop-map-stage">
      <div ref={elRef} className="shop-map" />
      {finding ? (
        <div className="shop-map-finding">
          <span className="shop-map-finding-spin" aria-hidden="true" />
          <span>Finding offers…</span>
        </div>
      ) : null}
      <button
        type="button"
        className={`shop-map-locate${locating ? ' is-loading' : ''}`}
        aria-label="My location"
        disabled={locating}
        onClick={recenter}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="3.2" />
          <path d="M12 3.5v2.2M12 18.3v2.2M3.5 12h2.2M18.3 12h2.2" />
          <circle cx="12" cy="12" r="7.2" />
        </svg>
      </button>
      {selected ? (
        <div className="shop-map-card">
          <span className="shop-map-card-logo">
            {selected.logo ? <img src={selected.logo} alt="" /> : <span />}
          </span>
          <span className="shop-map-card-copy">
            <strong>{selected.name}</strong>
            {cardLocation(selected) ? <em>{cardLocation(selected)}</em> : null}
            <button
              type="button"
              className="shop-map-directions"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setPicker((open) => !open);
              }}
            >
              Get Directions
            </button>
            {picker && links ? (
              <span className="shop-map-picker">
                <a href={links.google} target="_blank" rel="noreferrer">
                  Google Maps
                </a>
                <a href={links.apple} target="_blank" rel="noreferrer">
                  Apple Maps
                </a>
              </span>
            ) : null}
          </span>
        </div>
      ) : null}
    </div>
  );
}

export default ShopMap;
